<template>
  <article
    class="leader-col"
    :class="{ selected, 'is-rail': !showCatchup }"
    @click="onColClick"
  >
    <section class="leader-card">
      <header v-if="briefStatus" class="card-top is-brief">
        <div class="brief-top">
          <button
            v-if="checkable"
            type="button"
            class="pick-flag"
            :class="{ on: selected }"
            :aria-label="selected ? '已选' : '选择'"
            @click.stop="$emit('select', leader)"
          ><i v-if="selected" class="el-icon-check" /></button>
          <span class="name">{{ leader.name }}</span>
          <span class="chg brief-chg" :class="chgClass(leader.changePct)">{{ formatPct(leader.changePct) }}</span>
          <span class="ma5-flag" :class="aboveMa5 ? 'is-on' : 'is-off'">{{ aboveMa5 ? '√MA5' : 'MA5' }}</span>
        </div>
        <div class="brief-tags">
          <span v-if="flagNewHighToday" class="tag flag-hot">今日新高</span>
          <span v-if="isSecondWaveCandidate" class="tag wave2">二波龙头候选</span>
          <span v-if="flagTomorrowLuNewHigh" class="tag flag-hot">明涨停新高</span>
          <span v-if="flagNextFirstBoard" class="tag flag-first-node">明日首板节点</span>
        </div>
      </header>
      <header v-else class="card-top">
        <div class="rank-name">
          <button
            v-if="checkable"
            type="button"
            class="pick-flag"
            :class="{ on: selected }"
            :aria-label="selected ? '已选' : '选择'"
            @click.stop="$emit('select', leader)"
          ><i v-if="selected" class="el-icon-check" /></button>
          <span v-if="!briefStatus" class="rank" :title="'展示顺序，不代表正式龙头排名'">{{ rank }}</span>
          <div>
            <div class="name-line">
              <span class="name">{{ leader.name }}</span>
              <span class="code">{{ leader.code }}</span>
              <button
                v-if="showReplay"
                type="button"
                class="replay-btn"
                :class="{ on: replayOn }"
                :disabled="replayOn"
                title="双重概念可再加入一个板块"
                @click.stop="$emit('replay')"
              >{{ replayOn ? '已复现' : '复现' }}</button>
              <span v-if="flagTomorrowLuNewHigh" class="tag flag-hot">明日涨停即新高</span>
            </div>
            <div class="chg-line">
              <span v-if="leader.closePrice != null" class="px">{{ leader.closePrice }}</span>
              <span class="chg" :class="chgClass(leader.changePct)">{{ formatPct(leader.changePct) }}</span>
              <el-tooltip :content="priceTip" placement="top">
                <span class="scope">{{ leader.changePctScope === 'CLOSE' ? '收盘' : '当前' }}</span>
              </el-tooltip>
            </div>
          </div>
        </div>
        <div class="right-meta">
          <div class="board">{{ boardLabel }}</div>
          <div v-if="boardSubLabel" class="nday">{{ boardSubLabel }}</div>
        </div>
      </header>

      <div v-if="!briefStatus && isMonitorCard" class="status-row">
        <el-tooltip content="活跃=仍在监控；已退出=已自动退池" placement="top">
          <span class="tag" :class="membershipTagClass">{{ membershipLabelCn }}</span>
        </el-tooltip>
        <el-tooltip content="监控状态（研究/产品态），不是龙头身份" placement="top">
          <span class="tag" :class="stateTagClass">{{ lifecycleStateCn }}</span>
        </el-tooltip>
        <el-tooltip content="近20个交易日曾达4板" placement="top">
          <span class="tag cand">四板候选</span>
        </el-tooltip>
        <el-tooltip
          v-if="isSecondWaveCandidate"
          content="须曾断板≥2个交易日：二次入池，或在池且今日创近20日新高"
          placement="top"
        >
          <span class="tag wave2">二波龙头候选</span>
        </el-tooltip>
        <span v-if="flagNewHighToday" class="tag flag-hot">今日创新高</span>
        <span v-if="flagNextFirstBoard" class="tag flag-first-node">明日首板节点</span>
        <span v-if="flagLimitUpToday" class="tag flag-hot">今日涨停</span>
      </div>
      <div v-if="!briefStatus && !isMonitorCard" class="status-row">
        <el-tooltip content="涨停池参考机械名单；正式身份未物化" placement="top">
          <span class="tag id">涨停池参考</span>
        </el-tooltip>
        <span class="tag life">身份待接入</span>
      </div>

      <template v-if="showCatchup">
      <div class="state-ev" v-if="isMonitorCard">
        <div class="state-ev-h">状态依据（事实）</div>
        <div class="fact-lines">
          <div>进入监控：{{ leader.entryDate || '—' }}</div>
          <div>
            <span v-if="leader.daysInMonitor != null">在池 {{ leader.daysInMonitor }} 天</span>
            <span v-if="daysSince4bText"> · 距离4板：{{ daysSince4bText }}</span>
            <span v-if="!flagLimitUpToday"> · 今日未涨停</span>
          </div>
          <div>进入原因：{{ entryReasonTextCn }}</div>
          <div v-if="leader.exitDate" class="exit-mark">
            退池：{{ leader.exitDate }}
            <span v-if="exitReasonText && exitReasonText !== '—'"> · {{ exitReasonShort }}</span>
          </div>
          <div v-if="weakenExtraText" class="weaken-extra">{{ weakenExtraText }}</div>
        </div>
      </div>
      <div class="state-ev" v-else title="正式字段未物化">
        <div class="state-ev-h">状态依据（事实）</div>
        <div class="fact-lines">下一观察：待接入</div>
      </div>

      <div class="attrs">
        <div class="theme-note">题材参考（{{ themeSnapshotDate }}快照，非当日归属）</div>
        <div class="attr-line">
          <span class="k">系统主属性</span>
          <el-tooltip :content="'题材快照 ' + themeSnapshotDate + ' · 非当日归属，不参与入池/身份/补涨归因'" placement="top">
            <span class="sys-pill">{{ sysAttrLabelCn }}</span>
          </el-tooltip>
        </div>
        <div class="attr-line">
          <span class="k">人工主属性</span>
          <el-select
            :value="manualAttrId"
            size="mini"
            clearable
            filterable
            placeholder="概念（同补涨分析）"
            class="manual-select"
            @change="$emit('manual-attr', $event)"
          >
            <el-option
              v-for="c in conceptOptions"
              :key="c"
              :label="c"
              :value="c"
            />
          </el-select>
        </div>
        <div class="attr-chips">
          <button type="button" class="linkish" @click="$emit('open-attrs')">全部属性</button>
        </div>
      </div>
      </template>

      <div class="returns">
        <div class="ret"><span>10日总涨幅</span><b :class="d10TopClass">{{ returnPct('d10') }}</b></div>
        <div class="ret"><span>15日总涨幅</span><b :class="returnRankClass('d15')">{{ returnPct('d15') }}</b></div>
        <div class="ret"><span>20日总涨幅</span><b :class="returnRankClass('d20')">{{ returnPct('d20') }}</b></div>
      </div>
    </section>

    <CatchupPanel
      v-if="showCatchup"
      :rows="catchupRows"
      :tab="catchupTab"
      :expanded="catchupExpanded"
      :prefix="catchupPrefix"
      :catchup-status="catchupStatus"
      :selected-attr="manualAttrId"
      :leader-first-board="leaderFirstBoard"
      @change-tab="$emit('catchup-tab', $event)"
      @expand="$emit('catchup-expand', true)"
      @collapse="$emit('catchup-expand', false)"
      @open-row="$emit('open-catchup', $event)"
    />
  </article>
</template>

<script>
import CatchupPanel from './CatchupPanel.vue'
import { formatSignedPct } from '@/services/leaderCommandCenterAdapter'

export default {
  name: 'LeaderColumn',
  components: { CatchupPanel },
  props: {
    leader: { type: Object, required: true },
    rank: { type: Number, default: 1 },
    pinned: { type: Boolean, default: false },
    manualAttrId: { type: String, default: '' },
    conceptOptions: { type: Array, default: () => [] },
    catchupRows: { type: Array, default: () => [] },
    catchupTab: { type: String, default: 'list' },
    catchupExpanded: { type: Boolean, default: false },
    catchupPrefix: { type: String, default: '' },
    catchupStatus: { type: String, default: 'none' },
    leaderFirstBoard: { type: Object, default: null },
    /** 观察池默认 true；传播左栏可关 */
    showCatchup: { type: Boolean, default: true },
    selected: { type: Boolean, default: false },
    checkable: { type: Boolean, default: false },
    showReplay: { type: Boolean, default: false },
    replayOn: { type: Boolean, default: false },
    returnMeta: { type: Object, default: () => ({}) },
    catalog: { type: Object, default: () => ({}) },
    briefStatus: { type: Boolean, default: false }
  },
  computed: {
    themeSnapshotDate() {
      return (this.leader.systemPrimaryAttr && this.leader.systemPrimaryAttr.snapshotDate) || '2026-09-22'
    },
    isMonitorCard() {
      return (
        this.leader.cardKind === 'CANDIDATE' ||
        this.leader.cardKind === 'MONITORING' ||
        !!this.leader.lifecycleState ||
        !!this.leader.candidateStatus
      )
    },
    lifecycleState() {
      return (
        this.leader.lifecycleState ||
        this.leader.attentionLevel ||
        'ACTIVE_MONITORING'
      )
    },
    membershipLabel() {
      const m = String(this.leader.membership || '').toUpperCase()
      if (m === 'REMOVED' || String(this.lifecycleState).toUpperCase() === 'REMOVED_FROM_MONITORING') {
        return 'REMOVED'
      }
      return 'ACTIVE'
    },
    membershipLabelCn() {
      return this.membershipLabel === 'REMOVED' ? '已退出' : '活跃'
    },
    lifecycleStateCn() {
      const s = String(this.lifecycleState || '').toUpperCase()
      if (s === 'ACTIVE_MONITORING') return '监控中'
      if (s === 'HIGH_ATTENTION_REVIEW') return '重点观察'
      if (s === 'CANDIDATE_ENTERED') return '刚入池'
      if (s === 'WEAKENING_REVIEW') return '走弱复核'
      if (s === 'EXIT_REVIEW_PENDING') return '退出待核'
      if (s === 'REMOVED_FROM_MONITORING') return '已退池'
      if (s === 'NORMAL_TRACKING_REVIEW') return '跟踪观察'
      return '监控中'
    },
    membershipTagClass() {
      return this.membershipLabel === 'REMOVED' ? 'comp' : 'hi'
    },
    stateTagClass() {
      const s = String(this.lifecycleState || '').toUpperCase()
      if (s === 'HIGH_ATTENTION_REVIEW') return 'hi'
      if (s === 'CANDIDATE_ENTERED') return 'cand'
      if (s === 'WEAKENING_REVIEW' || s === 'EXIT_REVIEW_PENDING' || s === 'REMOVED_FROM_MONITORING') return 'comp'
      return 'track'
    },
    entryReasonText() {
      return this.leader.entryReason || 'HAS_4B_LAST_20D'
    },
    entryReasonTextCn() {
      const r = String(this.entryReasonText || '')
      if (r === 'MANUAL_ADD' || this.leader.manualAdd) return '手动添加'
      if (r === 'REENTRY_LIMIT_UP_RECOVER') return '破位退出后20日内涨停收回（二次入池）'
      if (r === 'REENTRY_20D_PRICE_NEWHIGH' || r.indexOf('REENTRY') === 0) {
        return '退出后20日内再破20日最高价（二次入池）'
      }
      if (r === 'CHINEXT_8D_3SEAL20') return '创业板8日内3次20%涨停封死'
      if (r === 'CHINEXT_8D_SEAL20_3YANG10') return '创业板8日内20%涨停封死，且另有3天≥10%阳线'
      if (r.indexOf('HAS_4B') === 0 || r.indexOf('4B') >= 0) return '近20个交易日内出现4板'
      return r
    },
    hadBoardBreakGe2() {
      if (this.leader.hadBoardBreakGe2 === true) return true
      const se = this.leader.stateEvidence
      const f = se && se.structureFacts && se.structureFacts.HAD_BOARD_BREAK_GE2
      return !!(f && (f.value === 'YES' || f.value === true))
    },
    isSecondWaveCandidate() {
      // 硬条件：自首次4B起曾连续断板≥2个交易日（排除九阳式连板新高）
      if (!this.hadBoardBreakGe2) return false
      const r = String(this.entryReasonText || '')
      if (r === 'REENTRY_20D_PRICE_NEWHIGH' || r.indexOf('REENTRY') === 0) return true
      if (String(this.leader.membership || '').toUpperCase() === 'ACTIVE' && this.flagNewHighToday) {
        return true
      }
      return false
    },
    sysAttrLabelCn() {
      const lab = this.leader.systemPrimaryAttr && this.leader.systemPrimaryAttr.label
      if (!lab || lab === 'MISSING' || lab === 'UNKNOWN') return '缺失'
      return lab
    },
    boardSubLabel() {
      return ''
    },
    flagNewHighToday() {
      const se = this.leader.stateEvidence
      const f = se && se.priceFacts && se.priceFacts.NEW_HIGH_TODAY
      if (f && (f.value === 'YES' || f.value === true)) return true
      const pe = this.leader.priceEvidence
      if (pe && (pe.newHigh === 'YES' || pe.newHigh === true)) return true
      return false
    },
    flagLimitUpToday() {
      const se = this.leader.stateEvidence
      const f = se && se.structureFacts && se.structureFacts.LIMIT_UP_TODAY
      if (f && (f.value === 'YES' || f.value === true)) return true
      if (this.leader.currentBoard != null && Number(this.leader.currentBoard) > 0) return true
      if (this.boardLabel && this.boardLabel !== '—' && /^\d+天\d+板$/.test(this.boardLabel)) return true
      return false
    },
    flagTomorrowLuNewHigh() {
      const se = this.leader.stateEvidence
      const f = se && se.priceFacts && se.priceFacts.TOMORROW_LU_IS_NEW_HIGH
      return !!(f && (f.value === 'YES' || f.value === true))
    },
    flagNextFirstBoard() {
      return this.leader.nextFirstBoardNode === true
    },
    aboveMa5() {
      return this.leader.aboveMa5 === true
    },
    d10TopClass() {
      const rank = this.leader.returnRank && this.leader.returnRank.d10
      return rank === 1 || rank === 2 ? 'rank-top2' : ''
    },
    daysSince4bText() {
      const se = this.leader.stateEvidence
      const f = se && se.heightFacts && se.heightFacts.DAYS_SINCE_4B
      if (f && f.value != null && Number.isFinite(Number(f.value))) return `${Number(f.value)}天`
      const hints = this.leader.evidenceHints || (se && se.displayHints) || []
      for (let i = 0; i < hints.length; i += 1) {
        const m = String(hints[i]).match(/距离4板：(\d+)天/)
        if (m) return `${m[1]}天`
      }
      return ''
    },
    weakenExtraText() {
      const s = String(this.lifecycleState || '').toUpperCase()
      if (s !== 'WEAKENING_REVIEW' && s !== 'EXIT_REVIEW_PENDING') return ''
      const parts = []
      const se = this.leader.stateEvidence
      const nh = se && se.weakeningFacts && se.weakeningFacts.DAYS_SINCE_LAST_NEW_HIGH
      if (nh && nh.value != null) parts.push(`距离新高：${nh.value}天`)
      const dd = se && se.weakeningFacts && se.weakeningFacts.DRAWDOWN_FROM_HIGH
      if (dd && dd.value != null) parts.push(`回撤：${(Number(dd.value) * 100).toFixed(1)}%`)
      return parts.join(' · ')
    },
    exitReasonText() {
      return (
        this.leader.exitReason ||
        (this.leader.exitInfo && this.leader.exitInfo.reason) ||
        '—'
      )
    },
    exitReasonShort() {
      const r = String(this.exitReasonText || '')
      if (r.indexOf('A_') === 0) return 'A·三板破位'
      if (r.indexOf('B_') === 0) return 'B·修复失败'
      if (r.indexOf('C_') === 0) return 'C·三连阴'
      if (r.indexOf('D_') === 0) return 'D·破MA30'
      return r
    },
    candidateReasonText() {
      const reasons = this.leader.attentionReason || this.leader.stateReason || []
      if (reasons.length) return reasons.join(' + ')
      if (String(this.lifecycleState).toUpperCase() === 'HIGH_ATTENTION_REVIEW') {
        return 'FOUR_BOARD + HIGH_ATTENTION_REVIEW'
      }
      return 'FOUR_BOARD'
    },
    priceTip() {
      const basis = this.leader.priceBasis || 'QFQ/RAW'
      const q = this.leader.changePctQuality || 'VALID'
      return '收盘价/涨跌幅 · ' + basis + ' · ' + q + ' · 非买卖信号'
    },
    boardLabel() {
      return (
        this.leader.boardTierLabel ||
        this.leader.boardStageLabel ||
        this.leader.nDayMBoard ||
        '—'
      )
    }
  },
  methods: {
    formatPct: formatSignedPct,
    returnPct(key) {
      const r = this.leader.returns || {}
      const meta = r[key + 'Meta']
      if (meta && meta.quality && meta.quality !== 'VALID') {
        if (meta.value == null) return meta.quality
      }
      if (r[key] === null || r[key] === undefined) return (r.quality && r.quality !== 'VALID') ? r.quality : '—'
      return formatSignedPct(r[key], 1)
    },
    returnRankClass(key) {
      const rr = this.leader.returnRank
      if (!rr) return ''
      const rank = rr[key]
      if (rank === 1 || rank === 2) return 'rank-top2'
      if (rank === 3) return 'rank-3'
      return ''
    },
    chgClass(v) {
      if (v > 0) return 'up'
      if (v < 0) return 'down'
      return ''
    },
    onColClick() {
      if (!this.showCatchup) this.$emit('select', this.leader)
    }
  }
}
</script>

<style lang="scss" scoped>
.leader-col {
  min-width: 280px;
  display: flex;
  flex-direction: column;
  &.is-rail {
    min-width: 0;
    cursor: pointer;
  }
  &.selected .leader-card {
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.22);
  }
}
.leader-card {
  background: #fff;
  border: 1px solid #d9e6f8;
  border-radius: 12px;
  padding: 10px 12px 8px;
  box-shadow: 0 2px 8px rgba(36, 99, 180, 0.06);
}
.card-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.card-top.is-brief { flex-direction: column; align-items: stretch; gap: 6px; }
.brief-top { display: flex; align-items: center; flex-wrap: nowrap; gap: 4px; width: 100%; }
.brief-top .name { margin-right: 0; flex: none; white-space: nowrap; }
.brief-chg { margin-left: 8px; flex: none; font-size: 16px; font-weight: 800; white-space: nowrap; }
.ma5-flag {
  margin-left: auto;
  flex: none;
  min-width: 52px;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.3;
  text-align: center;
  &.is-on { background: #16a34a; color: #fff; }
  &.is-off { background: #d1d5db; color: #6b7280; }
}
.brief-tags { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-start; gap: 6px; min-height: 26px; }
.leader-col.is-rail { height: 100%; }
.leader-col.is-rail .leader-card { height: 100%; display: flex; flex-direction: column; }
.leader-col.is-rail .returns { margin-top: auto; }
.rank-name { display: flex; gap: 8px; }
.pick-flag {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #1a73e8;
  background: #fff;
  color: #1a73e8;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &.on { background: #1a73e8; color: #fff; }
  i { font-size: 12px; font-weight: 700; }
}
.rank {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #1a73e8;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.name-line { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.name { font-size: 16px; font-weight: 800; color: #1a2b3c; }
.brief-chg { font-size: 14px; font-weight: 800; }
.code { font-size: 11px; color: #8aa0c2; }
.replay-btn {
  margin-left: 4px;
  height: 18px;
  padding: 0 6px;
  border: 1px solid #f5b4b4;
  border-radius: 9px;
  background: #fff5f5;
  color: #d64545;
  font-size: 11px;
  line-height: 16px;
  cursor: pointer;
}
.replay-btn.on {
  border-color: #d7e4f6;
  background: #f4f7fb;
  color: #8aa0c2;
  cursor: default;
}
.px { font-size: 13px; font-weight: 700; color: #35598c; margin-right: 6px; }
.chg { font-size: 18px; font-weight: 800; }
.chg.up { color: #e24b4b; }
.chg.down { color: #1aae6f; }
.scope { margin-left: 6px; font-size: 10px; color: #90a4c0; }
.right-meta { text-align: right; }
.board { font-size: 18px; font-weight: 800; color: #1a73e8; }
.nday { font-size: 11px; color: #7b93b5; }
.pin-btn {
  margin-top: 4px;
  border: 1px solid #f3b3b3;
  background: #fff5f5;
  color: #d45353;
  border-radius: 10px;
  font-size: 11px;
  padding: 2px 8px;
  cursor: pointer;
  &.on {
    background: #d45353;
    color: #fff;
    border-color: #d45353;
  }
}
.status-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.tag {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 6px;
  font-weight: 700;
}
.tag.id { background: #e8f1ff; color: #1a73e8; }
.tag.comp { background: #fff4e5; color: #c47b16; }
.tag.life { background: #eef6ee; color: #2f9d5b; }
.tag.cand { background: #e8f1ff; color: #1a73e8; }
.tag.wave2 {
  background: #ec407a;
  color: #fff;
  font-weight: 800;
}
.tag.hi { background: #ffe8e8; color: #c62828; }
.tag.track { background: #f0f3f7; color: #5b6b82; }
.tag.flag-hot {
  background: #e53935;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
  line-height: 1.2;
}
.tag.flag-first-node {
  background: linear-gradient(180deg, #ffb020 0%, #f97316 55%, #ea580c 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  border-radius: 6px;
  line-height: 1.2;
  text-shadow: 0 1px 0 rgba(120, 40, 0, 0.45);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.45), 0 0 10px rgba(249, 115, 22, 0.55);
  animation: first-node-glow 1.6s ease-in-out infinite;
}
@keyframes first-node-glow {
  0%, 100% { box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 0 6px rgba(249, 115, 22, 0.35); }
  50% { box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.7), 0 0 14px rgba(255, 176, 32, 0.95); }
}
.exit-mark { color: #c62828; font-weight: 700; }
.state-ev {
  margin-top: 8px;
  background: #f7faff;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 11px;
  color: #35598c;
}
.state-ev-h { font-weight: 700; margin-bottom: 4px; color: #1a73e8; display: block; }
.fact-lines {
  line-height: 1.45;
  color: #4a6280;
}
.weaken-extra {
  margin-top: 2px;
  color: #c47b16;
}
.abcd {
  margin-top: 8px;
  background: #f6f9ff;
  border-radius: 8px;
  padding: 6px 8px;
}
.abcd-h, .attr-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.abcd-h { font-size: 11px; color: #5b6b82; }
.abcd-body { margin-top: 4px; display: flex; justify-content: space-between; gap: 6px; align-items: center; }
.main-ev { font-size: 12px; font-weight: 700; color: #1d4f91; }
.hit {
  display: inline-block;
  margin-left: 4px;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 4px;
  background: #1a73e8;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
}
.attrs { margin-top: 8px; }
.theme-note {
  font-size: 10px;
  color: #ad6800;
  margin-bottom: 4px;
  line-height: 1.35;
}
.k { font-size: 11px; color: #7b93b5; }
.sys-pill {
  background: #eef3ff;
  color: #35598c;
  font-size: 11px;
  border-radius: 8px;
  padding: 1px 8px;
}
.manual-select { width: 140px; }
.attr-chips { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.chip {
  background: #f0f4fb;
  color: #4a6280;
  font-size: 11px;
  border-radius: 8px;
  padding: 1px 7px;
}
.linkish {
  border: 0;
  background: none;
  color: #1a73e8;
  cursor: pointer;
  font-size: 11px;
  padding: 0;
}
.marks {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}
.mark {
  font-size: 10px;
  color: #9aadc6;
  &.on { color: #1a73e8; font-weight: 700; }
}
.returns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 8px;
}
.ret {
  background: #f7f9fc;
  border-radius: 8px;
  padding: 6px 4px;
  text-align: center;
  span { display: block; font-size: 10px; color: #90a4c0; }
  b {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    color: #3a4d66;
    font-weight: 700;
    &.rank-top2 {
      color: #e91e8c;
      font-weight: 800;
    }
    &.rank-3 {
      color: #d32f2f;
      font-weight: 800;
    }
  }
}
</style>
